import { type MouseEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LogoMainHorizontal } from '@/components/brand';
import { ArrowDown, ArrowUp, MessageQuestion, ImportCurve } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { downloadBlob } from '@/utils/download';
import { clamp } from '@/utils/statistics';
import { CHART_Z_DOMAIN, formatPercentShare, type ScoreConversion } from '../utils';

const SVG_WIDTH = 920;
const SVG_HEIGHT = 420;
const CHART_LEFT = 56;
const CHART_RIGHT = 34;
const CHART_TOP = 30;
const CHART_BOTTOM = 86;
const Y_MAX = 1 / Math.sqrt(2 * Math.PI);
const GUIDE_ARROW_SIZE = 10;
const GUIDE_ARROW_HALF_HEIGHT = 5;

type BandTone = 'center' | 'shoulder' | 'tail' | 'extreme';

// CSS custom properties backing every color this chart paints. We resolve them
// to concrete `hsl(...)` strings at render time (see resolveVizColors) instead
// of using `hsl(var(--token))` directly, because the chart is exported to PNG
// via html-to-image and the cloned/serialized SVG would not carry the document
// :root variables. The distribution bands use the dedicated --viz-band-* ramp
// (center -> tails); everything else maps to standard semantic tokens.
const VIZ_TOKEN_VARS = {
    center: '--viz-band-1',
    shoulder: '--viz-band-2',
    tail: '--viz-band-3',
    extreme: '--viz-band-4',
    marker: '--warning',
    surface: '--surface',
    foreground: '--foreground',
    muted: '--muted-foreground',
    background: '--background',
} as const;

type VizColors = Record<keyof typeof VIZ_TOKEN_VARS, string>;

const FALLBACK_VIZ_COLORS = Object.fromEntries(
    Object.entries(VIZ_TOKEN_VARS).map(([key, cssVar]) => [key, `hsl(var(${cssVar}))`]),
) as VizColors;

function resolveVizColors(): VizColors {
    if (typeof window === 'undefined') {
        return FALLBACK_VIZ_COLORS;
    }

    const computed = getComputedStyle(document.documentElement);

    return Object.fromEntries(
        Object.entries(VIZ_TOKEN_VARS).map(([key, cssVar]) => {
            const value = computed.getPropertyValue(cssVar).trim();
            return [key, value ? `hsl(${value})` : `hsl(var(${cssVar}))`];
        }),
    ) as VizColors;
}

const BANDS = [
    {
        id: 'extreme-left',
        start: -3.4,
        end: -3,
        tone: 'extreme' as BandTone,
        opacity: 1,
        title: 'Extremo inferior',
        rangeLabel: 'Abaixo de -3 DP',
        shareLabel: 'Aprox. 0,1% da populacao',
        description: 'Resultados extremamente raros e muito abaixo da media padronizada.',
    },
    {
        id: 'tail-left',
        start: -3,
        end: -2,
        tone: 'tail' as BandTone,
        opacity: 1,
        title: 'Cauda inferior',
        rangeLabel: 'Entre -3 e -2 DP',
        shareLabel: 'Aprox. 2,1% da populacao',
        description:
            'Faixa rara, ainda abaixo da media, mas menos extrema do que o limite final da curva.',
    },
    {
        id: 'shoulder-left',
        start: -2,
        end: -1,
        tone: 'shoulder' as BandTone,
        opacity: 1,
        title: 'Faixa inferior intermediaria',
        rangeLabel: 'Entre -2 e -1 DP',
        shareLabel: 'Aprox. 13,6% da populacao',
        description:
            'Zona abaixo da media, mas ainda em um intervalo relativamente frequente na distribuicao.',
    },
    {
        id: 'center',
        start: -1,
        end: 1,
        tone: 'center' as BandTone,
        opacity: 1,
        title: 'Faixa central',
        rangeLabel: 'Entre -1 e +1 DP',
        shareLabel: 'Aprox. 68,2% da populacao',
        description:
            'Concentracao principal da distribuicao normal, em torno da media padronizada.',
    },
    {
        id: 'shoulder-right',
        start: 1,
        end: 2,
        tone: 'shoulder' as BandTone,
        opacity: 1,
        title: 'Faixa superior intermediaria',
        rangeLabel: 'Entre +1 e +2 DP',
        shareLabel: 'Aprox. 13,6% da populacao',
        description:
            'Zona acima da media, ainda dentro de um intervalo relativamente frequente da curva.',
    },
    {
        id: 'tail-right',
        start: 2,
        end: 3,
        tone: 'tail' as BandTone,
        opacity: 1,
        title: 'Cauda superior',
        rangeLabel: 'Entre +2 e +3 DP',
        shareLabel: 'Aprox. 2,1% da populacao',
        description: 'Faixa rara, acima da media, mas menos extrema do que a ponta final da curva.',
    },
    {
        id: 'extreme-right',
        start: 3,
        end: 3.4,
        tone: 'extreme' as BandTone,
        opacity: 1,
        title: 'Extremo superior',
        rangeLabel: 'Acima de +3 DP',
        shareLabel: 'Aprox. 0,1% da populacao',
        description: 'Resultados extremamente raros e muito acima da media padronizada.',
    },
];

const SEGMENT_LABELS = [
    { midpoint: -3.2, label: '0,1%', offset: 18 },
    { midpoint: -2.5, label: '2%', offset: 20 },
    { midpoint: -1.5, label: '14%', offset: 24 },
    { midpoint: -0.5, label: '34%', offset: 26 },
    { midpoint: 0.5, label: '34%', offset: 26 },
    { midpoint: 1.5, label: '14%', offset: 24 },
    { midpoint: 2.5, label: '2%', offset: 20 },
    { midpoint: 3.2, label: '0,1%', offset: 18 },
];

const INTERVAL_ANNOTATIONS = [
    { start: -1, end: 1, label: '68%' },
    { start: -2, end: 2, label: '95%' },
];

function normalDensity(z: number): number {
    return (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-(z * z) / 2);
}

function buildRange(start: number, end: number, step = 0.05): number[] {
    const values: number[] = [];

    for (let current = start; current < end; current += step) {
        values.push(Number(current.toFixed(4)));
    }

    values.push(end);

    return values;
}

function buildArrowhead(x: number, y: number, direction: 'left' | 'right'): string {
    const baseX = direction === 'left' ? x + GUIDE_ARROW_SIZE : x - GUIDE_ARROW_SIZE;

    return `${x},${y} ${baseX},${y - GUIDE_ARROW_HALF_HEIGHT} ${baseX},${y + GUIDE_ARROW_HALF_HEIGHT}`;
}

export default function NormalDistributionChart({
    conversion,
    onZChange,
    onOpenReference,
}: {
    conversion: ScoreConversion | null;
    onZChange?: (z: number) => void;
    onOpenReference?: () => void;
}) {
    const xMin = CHART_Z_DOMAIN[0];
    const xMax = CHART_Z_DOMAIN[1];
    const plotWidth = SVG_WIDTH - CHART_LEFT - CHART_RIGHT;
    const plotHeight = SVG_HEIGHT - CHART_TOP - CHART_BOTTOM;
    const baselineY = CHART_TOP + plotHeight;
    const watermarkWidth = 84;
    const watermarkHeight = 19;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [vizColors, setVizColors] = useState<VizColors>(resolveVizColors);
    const [isExporting, setIsExporting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [hoveredBand, setHoveredBand] = useState<{
        id: string;
        title: string;
        rangeLabel: string;
        shareLabel: string;
        description: string;
        x: number;
        y: number;
    } | null>(null);

    // Re-resolve tokens after mount in case the stylesheet applied after the
    // first paint; keeps the chart driven by the design tokens in index.css.
    useEffect(() => {
        setVizColors(resolveVizColors());
    }, []);

    const toX = (value: number) => CHART_LEFT + ((value - xMin) / (xMax - xMin)) * plotWidth;
    const toY = (value: number) => CHART_TOP + (1 - value / Y_MAX) * plotHeight;

    const curvePoints = buildRange(xMin, xMax).map((z) => ({
        z,
        x: toX(z),
        y: toY(normalDensity(z)),
    }));

    const curvePath = curvePoints
        .map((point, index) => {
            const command = index === 0 ? 'M' : 'L';
            return `${command}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
        })
        .join(' ');

    const bandPolygons = BANDS.map((band) => {
        const points = buildRange(band.start, band.end).map((z) => ({
            x: toX(z),
            y: toY(normalDensity(z)),
        }));

        const polygon = [
            `${toX(band.start).toFixed(2)},${baselineY.toFixed(2)}`,
            ...points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`),
            `${toX(band.end).toFixed(2)},${baselineY.toFixed(2)}`,
        ].join(' ');

        return {
            ...band,
            polygon,
        };
    });

    const activeZ = clamp(conversion?.z ?? 0, xMin, xMax);
    const activeDensity = normalDensity(activeZ);
    const markerX = toX(activeZ);
    const markerY = toY(activeDensity);
    const formattedBelow = conversion ? formatPercentShare(conversion.below) : '50,00%';
    const formattedAbove = conversion ? formatPercentShare(conversion.above) : '50,00%';

    const clientXToZ = (clientX: number) => {
        const rect = svgRef.current?.getBoundingClientRect();

        if (!rect) {
            return null;
        }

        const normalizedX = ((clientX - rect.left) / rect.width) * SVG_WIDTH;
        const clampedX = clamp(normalizedX, CHART_LEFT, SVG_WIDTH - CHART_RIGHT);

        return xMin + ((clampedX - CHART_LEFT) / plotWidth) * (xMax - xMin);
    };

    const updateDraggedZ = (clientX: number) => {
        if (!onZChange) {
            return;
        }

        const nextZ = clientXToZ(clientX);

        if (nextZ === null) {
            return;
        }

        onZChange(nextZ);
    };

    const updateHoveredBand = (
        band: (typeof BANDS)[number],
        event: MouseEvent<SVGPolygonElement>,
    ) => {
        if (isDragging) {
            return;
        }

        const rect = containerRef.current?.getBoundingClientRect();

        if (!rect) {
            return;
        }

        const nextX = clamp(event.clientX - rect.left + 14, 14, rect.width - 258);
        const nextY = clamp(event.clientY - rect.top + 14, 14, rect.height - 130);

        setHoveredBand({
            id: band.id,
            title: band.title,
            rangeLabel: band.rangeLabel,
            shareLabel: band.shareLabel,
            description: band.description,
            x: nextX,
            y: nextY,
        });
    };

    const handleMarkerPointerDown = (event: PointerEvent<SVGRectElement>) => {
        if (!onZChange) {
            return;
        }

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setHoveredBand(null);
        setIsDragging(true);
        updateDraggedZ(event.clientX);
    };

    const handleMarkerPointerMove = (event: PointerEvent<SVGRectElement>) => {
        if (!isDragging) {
            return;
        }

        updateDraggedZ(event.clientX);
    };

    const handleMarkerPointerEnd = (event: PointerEvent<SVGRectElement>) => {
        if (!isDragging) {
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        setIsDragging(false);
    };

    const handleDownloadChart = async () => {
        if (!containerRef.current || isExporting) {
            return;
        }

        setHoveredBand(null);
        setIsExporting(true);

        try {
            await new Promise<void>((resolve) => {
                window.requestAnimationFrame(() => resolve());
            });

            const { toBlob } = await import('html-to-image');

            const blob = await toBlob(containerRef.current, {
                backgroundColor: vizColors.background,
                cacheBust: true,
                pixelRatio: 2,
                filter: (node) => {
                    return !(node instanceof HTMLElement && node.dataset.exportIgnore === 'true');
                },
            });

            if (!blob) {
                throw new Error('chart-export-empty');
            }

            downloadBlob(blob, 'grafico_score_z.png');
            toast.success('Grafico exportado com sucesso.');
        } catch {
            toast.error('Nao foi possivel exportar o grafico.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div
            ref={containerRef}
            className="relative overflow-hidden rounded-4xl border border-border bg-background text-foreground shadow-elevation-2"
        >
            <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-7">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                                    Leitura na curva normal
                                </h3>
                                {onOpenReference && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 rounded-full text-muted-foreground hover:bg-surface hover:text-foreground"
                                        onClick={onOpenReference}
                                        aria-label="Abrir referencia rapida"
                                        data-export-ignore="true"
                                    >
                                        <MessageQuestion className="size-4" />
                                    </Button>
                                )}
                            </div>
                            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                                Veja onde o resultado se posiciona na distribuição e use a curva
                                para explicar a equivalência entre as escalas.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="rounded-3xl border border-border bg-surface px-4 py-3 text-sm text-foreground sm:min-w-[232px]">
                            <div className="flex items-center justify-between gap-4 whitespace-nowrap text-lg font-semibold tracking-tight text-foreground">
                                <div className="flex items-center gap-1.5">
                                    <ArrowDown
                                        aria-hidden="true"
                                        className="size-4 text-muted-foreground"
                                    />
                                    <span>{formattedBelow}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <ArrowUp
                                        aria-hidden="true"
                                        className="size-4 text-muted-foreground"
                                    />
                                    <span>{formattedAbove}</span>
                                </div>
                            </div>
                        </div>

                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-11 shrink-0 rounded-2xl border border-border bg-surface text-muted-foreground hover:bg-background hover:text-foreground"
                            onClick={handleDownloadChart}
                            disabled={isExporting}
                            aria-label="Baixar grafico em PNG"
                            data-export-ignore="true"
                        >
                            <ImportCurve className="size-5" />
                        </Button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <div className="relative min-w-[760px]">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute z-0 overflow-hidden opacity-30"
                            style={{
                                left: `${(CHART_LEFT / SVG_WIDTH) * 100}%`,
                                top: `${((CHART_TOP + plotHeight / 2 - watermarkHeight / 2) / SVG_HEIGHT) * 100}%`,
                                width: `${(watermarkWidth / SVG_WIDTH) * 100}%`,
                                height: `${(watermarkHeight / SVG_HEIGHT) * 100}%`,
                            }}
                        >
                            <div
                                style={{
                                    transform: 'scale(0.5833)',
                                    transformOrigin: 'top left',
                                }}
                            >
                                <LogoMainHorizontal />
                            </div>
                        </div>

                        <svg
                            ref={svgRef}
                            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                            className="relative z-10 block w-full min-w-[760px]"
                            role="img"
                            aria-label="Curva normal com destaque do escore atual"
                        >
                            <defs>
                                <clipPath id="normalDistributionPlotClip">
                                    <rect
                                        x={CHART_LEFT}
                                        y={CHART_TOP}
                                        width={plotWidth}
                                        height={plotHeight}
                                    />
                                </clipPath>
                                <filter id="markerGlow">
                                    <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            <line
                                x1={CHART_LEFT}
                                y1={baselineY}
                                x2={SVG_WIDTH - CHART_RIGHT}
                                y2={baselineY}
                                stroke={vizColors.muted}
                                strokeOpacity="0.38"
                                strokeWidth="1.25"
                            />

                            {[-3, -2, -1, 0, 1, 2, 3].map((tick) => {
                                const tickX = toX(tick);

                                return (
                                    <g key={tick}>
                                        <line
                                            x1={tickX}
                                            y1={CHART_TOP + 4}
                                            x2={tickX}
                                            y2={baselineY + 12}
                                            stroke={
                                                tick === 0 ? vizColors.foreground : vizColors.muted
                                            }
                                            strokeOpacity={tick === 0 ? '0.9' : '0.24'}
                                            strokeDasharray={tick === 0 ? '0' : '4 7'}
                                            strokeWidth={tick === 0 ? '1.4' : '1'}
                                        />
                                        <text
                                            x={tickX}
                                            y={baselineY + 34}
                                            fill={vizColors.muted}
                                            fontSize="16"
                                            fontWeight={600}
                                            textAnchor="middle"
                                        >
                                            {tick}
                                        </text>
                                    </g>
                                );
                            })}

                            {bandPolygons.map((band) => (
                                <polygon
                                    key={`${band.start}-${band.end}`}
                                    points={band.polygon}
                                    fill={vizColors[band.tone]}
                                    opacity={band.opacity}
                                    stroke={
                                        !isDragging && hoveredBand?.id === band.id
                                            ? vizColors.foreground
                                            : 'transparent'
                                    }
                                    strokeWidth={
                                        !isDragging && hoveredBand?.id === band.id ? '1.5' : '0'
                                    }
                                    className="cursor-pointer transition-opacity"
                                    onMouseEnter={(event) => updateHoveredBand(band, event)}
                                    onMouseMove={(event) => updateHoveredBand(band, event)}
                                    onMouseLeave={() => setHoveredBand(null)}
                                    aria-label={`${band.title}. ${band.rangeLabel}. ${band.shareLabel}.`}
                                />
                            ))}

                            <path
                                d={curvePath}
                                fill="none"
                                stroke={vizColors.foreground}
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />

                            {SEGMENT_LABELS.map((item) => {
                                const labelX = toX(item.midpoint);
                                const labelY = clamp(
                                    toY(normalDensity(item.midpoint)) - item.offset,
                                    CHART_TOP + 10,
                                    baselineY - 12,
                                );

                                return (
                                    <text
                                        key={item.label + item.midpoint}
                                        x={labelX}
                                        y={labelY}
                                        fill={
                                            Math.abs(item.midpoint) < 2
                                                ? vizColors.foreground
                                                : vizColors.muted
                                        }
                                        fontSize="20"
                                        fontWeight={800}
                                        textAnchor="middle"
                                    >
                                        {item.label}
                                    </text>
                                );
                            })}

                            {INTERVAL_ANNOTATIONS.map((annotation) => {
                                const boundaryZ = Math.max(
                                    Math.abs(annotation.start),
                                    Math.abs(annotation.end),
                                );
                                const lineY = toY(normalDensity(boundaryZ));
                                const startTipX = toX(annotation.start);
                                const endTipX = toX(annotation.end);
                                const lineStartX = startTipX + GUIDE_ARROW_SIZE;
                                const lineEndX = endTipX - GUIDE_ARROW_SIZE;
                                const centerX = (lineStartX + lineEndX) / 2;

                                return (
                                    <g
                                        key={annotation.label}
                                        clipPath="url(#normalDistributionPlotClip)"
                                    >
                                        <line
                                            x1={lineStartX}
                                            y1={lineY}
                                            x2={lineEndX}
                                            y2={lineY}
                                            stroke={vizColors.surface}
                                            strokeWidth="2"
                                        />
                                        <polygon
                                            points={buildArrowhead(startTipX, lineY, 'left')}
                                            fill={vizColors.surface}
                                        />
                                        <polygon
                                            points={buildArrowhead(endTipX, lineY, 'right')}
                                            fill={vizColors.surface}
                                        />
                                        <text
                                            x={centerX}
                                            y={lineY - 9}
                                            fill={vizColors.surface}
                                            fontSize="20"
                                            fontWeight={800}
                                            textAnchor="middle"
                                        >
                                            {annotation.label}
                                        </text>
                                    </g>
                                );
                            })}

                            <line
                                x1={markerX}
                                y1={CHART_TOP + 12}
                                x2={markerX}
                                y2={baselineY}
                                stroke={vizColors.marker}
                                strokeWidth="2.5"
                                strokeDasharray="8 8"
                                opacity="0.9"
                            />

                            {onZChange && (
                                <rect
                                    x={markerX - 24}
                                    y={CHART_TOP}
                                    width="48"
                                    height={plotHeight + 12}
                                    rx="24"
                                    fill="transparent"
                                    className={isDragging ? 'cursor-grabbing' : 'cursor-ew-resize'}
                                    style={{ touchAction: 'none' }}
                                    onPointerDown={handleMarkerPointerDown}
                                    onPointerMove={handleMarkerPointerMove}
                                    onPointerUp={handleMarkerPointerEnd}
                                    onPointerCancel={handleMarkerPointerEnd}
                                    onLostPointerCapture={() => setIsDragging(false)}
                                    aria-label="Arraste para ajustar o escore Z diretamente na curva"
                                />
                            )}

                            <circle
                                cx={markerX}
                                cy={markerY}
                                r="8"
                                fill={vizColors.marker}
                                filter="url(#markerGlow)"
                            />
                            <circle cx={markerX} cy={markerY} r="4" fill={vizColors.surface} />

                            <text
                                x={SVG_WIDTH / 2}
                                y={SVG_HEIGHT - 20}
                                fill={vizColors.muted}
                                fontSize="16"
                                fontWeight={600}
                                textAnchor="middle"
                            >
                                Eixo Z padronizado
                            </text>
                        </svg>
                    </div>
                </div>
            </div>

            {hoveredBand && (
                <div
                    className="pointer-events-none absolute z-30 w-60 rounded-2xl border border-border bg-surface px-4 py-3 shadow-elevation-1"
                    style={{ left: hoveredBand.x, top: hoveredBand.y }}
                    data-export-ignore="true"
                >
                    <p className="text-sm font-semibold text-foreground">{hoveredBand.title}</p>
                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                        {hoveredBand.rangeLabel}
                    </p>
                    <p className="mt-3 text-sm font-medium text-foreground">
                        {hoveredBand.shareLabel}
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                        {hoveredBand.description}
                    </p>
                </div>
            )}
        </div>
    );
}
